import { Component, OnInit } from "@angular/core";
import { NgbCalendar, NgbDatepickerI18n } from "@ng-bootstrap/ng-bootstrap";
import {
    CustomDatepickerI18nService,
    I18n,
} from "@app/shared/services/custom-datepicker-i18n.service";
import { FlightService } from "@app/shared/services/flight.service";
import { ShareVars } from "@app/shared/services/share.vars";
import { environment } from "environments/environment";
import { NavigationService } from "@app/shared/services/navigation.service";
import { HotelService } from "@app/shared/services/hotel.service";
import { finalize, first } from "rxjs/operators";
import { BaseService } from "@app/shared/services/base.service";
import { DateService } from "@app/shared/services/date.service";

interface FlightBooking {
    pnr: string;
    from: string;
    to: string;
    date: string;
}

interface HotelBooking {
    reference: string;
    hotelName: string;
    checkInDate: string;
    checkOutDate: string;
    bookingDate: string;
    hotelImage: string;
    price: number;
    currency: string;
    status: string;
    type: string;
}

interface FlightFilters {
    pnr: string;
    departure: string;
    arrival: string;
    firstDay: string;
    lastDay: string;
    dateType: string;
}

interface HotelFilters {
    reference: string;
    hotelName: string;
    firstDay: string;
    lastDay: string;
    dateType: string;
}

interface SortConfig {
    column: string;
    direction: "asc" | "desc";
}

@Component({
    selector: "app-b2c-bookings",
    templateUrl: "./b2c-bookings.component.html",
    styleUrls: ["./b2c-bookings.component.scss"],
    providers: [
        I18n,
        { provide: NgbDatepickerI18n, useClass: CustomDatepickerI18nService },
    ],
})
export class B2cBookingsComponent implements OnInit {
    newName;
    newPnr;

    showEditTicketModal = false;

    // common

    airlineDict;
    airportDict;

    activeTab: "flight" | "hotel" = "flight";
    clientAssets;
    cabinClass = {
        I: "INDIFFERENT",
        M: "ECONOMY_STANDARD",
        Y: "ECONOMY",
        W: "ECONOMY_PREMIUM",
        C: "BUSINESS",
        F: "FIRST",
        X: "MULTIPLE",
    };

    // flight
    flightsLoading = false;
    flightBookingsList = [];
    flightFilters: FlightFilters = {
        pnr: "",
        departure: "",
        arrival: "",
        firstDay: "",
        lastDay: "",
        dateType: "BOOKING",
    };
    flightSort: SortConfig = { column: "date", direction: "desc" };

    // Pagination flight
    totalFlightPages: number = 0;
    currentFlightPage: number = 1;
    itemsPerPageFlight: number = 10;
    totalFlightBookings: number = 0;
    flightPaginationStart: number = 0;
    flightPaginationEnd: number = 0;

    // hotel
    hotelsLoading = false;
    hotelBookingsList: HotelBooking[] = [];
    hotelFilters: HotelFilters = {
        reference: "",
        hotelName: "",
        firstDay: "",
        lastDay: "",
        dateType: "BOOKING",
    };
    hotelSort: SortConfig = { column: "date", direction: "desc" };

    // Pagination hotel
    totalHotelPages: number = 0;
    currentHotelPage: number = 1;
    itemsPerPageHotel: number = 10;
    totalHotelBookings: number = 0;

    perPageList: number[] = [2, 5, 10, 20, 50];

    Math = Math;

    today;

    constructor(
        private flightService: FlightService,
        private hotelService: HotelService,
        private navigationService: NavigationService,
        private baseService: BaseService,
        private calendar: NgbCalendar,
    ) {}

    ngOnInit() {
        this.clientAssets = environment.assets;
        ShareVars.isBusy = false;
        this.today = this.calendar.getToday();
        this.getFlightBookings();
    }

    showTabs() {
        return (
            ShareVars.checkAuthorization("AUTHORIZED_TO_USE_FLIGHT_BOOKING") &&
            ShareVars.checkAuthorization("AUTHORIZED_TO_USE_HOTEL_BOOKING")
        );
    }

    showFlightTab() {
        return ShareVars.checkAuthorization("AUTHORIZED_TO_USE_FLIGHT_BOOKING");
    }

    shoHotelTab() {
        return ShareVars.checkAuthorization("AUTHORIZED_TO_USE_HOTEL_BOOKING");
    }

    getFlightBookings() {
        if (this.flightsLoading) return;
        this.flightsLoading = true;
        this.initBeforeSearch();

        this.flightService
            .getFlightBookingsB2c({
                pnr: this.flightFilters.pnr.trim(),
                date_type: "BOOKING",
                firstDay: this.flightFilters.firstDay
                    ? DateService.toStringDate(this.flightFilters.firstDay)
                    : null,
                lastDay: this.flightFilters.lastDay
                    ? DateService.toStringDate(this.flightFilters.lastDay)
                    : null,
            })
            .subscribe(
                (data: any) => {
                    this.flightsLoading = false;
                    if (data && data.data && data.data.success) {
                        this.flightBookingsList = data.data.data;
                        this.airlineDict = data.data.airline;
                        this.airportDict = data.data.airport;
                        this.totalFlightBookings =
                            this.flightBookingsList.length;
                        this.changePageFlight(1);
                    }
                },
                (error) => {
                    this.flightsLoading = false;
                },
            );
    }

    applyFlightFilters() {
        this.getFlightBookings();
    }

    clearFlightFilters() {
        this.flightFilters = {
            pnr: "",
            departure: "",
            arrival: "",
            firstDay: "",
            lastDay: "",
            dateType: "BOOKING",
        };
        this.getFlightBookings();
    }

    changePageFlight(page: number) {
        this.currentFlightPage = page;
        this.flightPaginationStart =
            (this.currentFlightPage - 1) * this.itemsPerPageFlight;
        this.flightPaginationEnd =
            this.flightPaginationStart + this.itemsPerPageFlight;
        this.totalFlightPages = Math.ceil(
            this.totalFlightBookings / this.itemsPerPageFlight,
        );
    }

    setItemPerPageFlight(itemsPerPage: number) {
        this.itemsPerPageFlight = +itemsPerPage;
        this.changePageFlight(1);
    }

    openAddTicketModal() {
        this.showEditTicketModal = true;
    }

    closeAddTicketModal() {
        this.showEditTicketModal = false;
    }

    canAddTicket() {
        return (
            this.newName &&
            this.newName.trim().length >= 3 &&
            this.newPnr &&
            this.newPnr.trim().length == 6
        );
    }

    addTicket() {
        if (!this.canAddTicket()) return;
        this.flightService
            .addExistingTicket({
                name: this.newName.trim(),
                pnr: this.newPnr.trim(),
            })
            .subscribe(
                (data) => {
                    console.log("data", data);
                    if (data.success) {
                        this.getFlightBookings();
                        this.showEditTicketModal = false;
                    }
                },
                (err) => {
                    console.log("err", err);
                },
            );
    }

    viewFlightBooking(pnr,name) {
        if(!pnr || !name) {
            // msg
            return;
        }
        window.open(
            `/edit-flight-bookings/eticket?pnr=${pnr}&name=${name}`,
            "_blank",
        );
    }

    // Hotel methods

    getHotelBookings() {
        if (this.hotelsLoading) return;
        // ShareVars.isBusy = true;
        this.hotelsLoading = true;
        this.initBeforeSearch();
        this.hotelService
            .getHotelBookingsB2c({
                reference: this.hotelFilters.reference.trim(),
                hotelName: this.hotelFilters.hotelName.trim(),
                firstDay: this.hotelFilters.firstDay
                    ? DateService.toStringDate(this.hotelFilters.firstDay)
                    : null,
                lastDay: this.hotelFilters.lastDay
                    ? DateService.toStringDate(this.hotelFilters.lastDay)
                    : null,
                date_type: this.hotelFilters.dateType,
                page: this.currentHotelPage,
                per_page: this.itemsPerPageHotel,
            })
            .pipe(
                finalize(() => {
                    this.hotelsLoading = false;
                    ShareVars.isBusy = false;
                }),
            )
            .subscribe(
                (data: any) => {
                    this.hotelBookingsList = data.data.data.map(
                        (item: any) => ({
                            reference: item.reference_number,
                            hotelName: item.hotel_name,
                            checkInDate: item.checkin,
                            checkOutDate: item.checkout,
                            bookingDate: item.created_at,
                            hotelImage:
                                item.hotel_image ||
                                "/assets/images/hotel-not-found.jpg",
                            price: item.total_price,
                            currency: item.currency || "DZD",
                            status: item.status,
                            type: item.type,
                        }),
                    );
                    this.totalHotelBookings = data.data.total;
                    this.totalHotelPages = Math.ceil(this.totalHotelBookings / this.itemsPerPageHotel);
                },
                (error) => {
                    console.log("Error fetching hotel bookings:", error);
                },
            );
    }

    applyHotelFilters() {
        this.currentHotelPage = 1;
        this.getHotelBookings();
    }

    clearHotelFilters() {
        this.currentHotelPage = 1;
        this.hotelFilters = {
            reference: "",
            hotelName: "",
            firstDay: "",
            lastDay: "",
            dateType: "BOOKING",
        };
    }

    changePageHotel(page: number) {
        this.currentHotelPage = page;
        this.getHotelBookings();
    }

    setItemPerPageHotel(itemsPerPage: number) {
        this.itemsPerPageHotel = itemsPerPage;
        this.currentHotelPage = 1;
        this.getHotelBookings();
    }

    viewHotelBooking(id) {
        if (!id) {
            // msg
            return;
        }
        window.open(
            `/b2c-bookings/hotel/voucher/${id}`,
            "_blank",
        );
    }

    // common methods

    initBeforeSearch() {
        if (this.activeTab === "hotel") {
            this.hotelBookingsList = [];
            this.totalHotelBookings = 0;
            this.totalHotelPages = 0;
        } else {
            this.flightBookingsList = [];
            this.totalFlightBookings = 0;
            this.totalFlightPages = 0;
        }
    }

    changeTab(tab: "flight" | "hotel") {
        this.activeTab = tab;
        if (tab === "flight" && !this.totalFlightBookings) {
            this.getFlightBookings();
        } else if (tab === "hotel" && !this.totalHotelBookings) {
            this.getHotelBookings();
        }
    }

    onFromDateChange(date) {
        this.hotelFilters.firstDay = date;

        if (
            date &&
            this.hotelFilters.lastDay &&
            this.compareDates(this.hotelFilters.lastDay, date) < 0
        ) {
            this.hotelFilters.lastDay = null;
        }
    }

    compareDates(a, b) {
        if (a.year !== b.year) {
            return a.year - b.year;
        }
        if (a.month !== b.month) {
            return a.month - b.month;
        }
        return a.day - b.day;
    }

    formatPrice(price) {
        return this.baseService.formatPrice(price);
    }

    isArabic() {
        return this.baseService.isArabic();
    }
}
